/**
 * Parse Excel (.xlsx / .xls) mod lists in the browser before analyze API.
 */

import * as XLSX from 'xlsx';

export const MAX_EXCEL_BYTES = 5 * 1024 * 1024;

export type ExcelExtractFormat = 'structured_json' | 'csv' | 'build_list';

export interface ExcelExtractResult {
  text: string;
  format: ExcelExtractFormat;
  rowCount: number;
  sheetName: string;
}

type ModField = 'title' | 'cost' | 'category' | 'vendor' | 'install_date' | 'part_number' | 'notes' | 'specs' | 'group';

const COLUMN_ALIASES: Record<ModField, string[]> = {
  title: [
    'title',
    'name',
    'part',
    'part name',
    'part_name',
    'part description',
    'item',
    'modification',
    'mod',
    'product',
    'description',
  ],
  // Do not alias plain "total" — that often labels a summary column, not unit price.
  cost: ['cost', 'price', 'amount', 'spent', 'paid', 'msrp', 'part cost', 'unit cost'],
  category: ['category', 'type', 'cat', 'class'],
  vendor: ['vendor', 'brand', 'manufacturer', 'shop', 'store', 'supplier', 'retailer'],
  install_date: ['install date', 'install_date', 'date', 'installed', 'purchase date', 'purchased', 'installed on'],
  part_number: ['part number', 'part_number', 'part #', 'part#', 'sku', 'model number', 'model #'],
  notes: ['notes', 'note', 'comments', 'comment'],
  specs: ['specs', 'specifications', 'spec', 'details', 'size', 'sizes'],
  group: ['group', 'section', 'parent', 'parent group', 'subsystem'],
};

const SKIP_SHEET_NAME = /^(fluids?|media|links?|photos?|images?|notes?|readme)$/i;

/** Skip spreadsheet total / tax / shipping / section-subtotal rows. */
export function isNonPartSummaryRow(title: string): boolean {
  const norm = title.trim().toLowerCase().replace(/:+\s*$/, '');
  if (!norm) return true;
  return /^(total|subtotal|grand total|sum|tax|sales tax|shipping|balance due|amount due|invoice total|order total|complete cost|total cost|labor(?: cost)?|install(?:ation)?(?: cost)?|misc(?:ellaneous)? total|parts total|section total)$/i.test(
    norm
  );
}

/** Section headers like "Engine and Bay:" or "Turbo Kit:" (usually no unit price). */
export function isSectionHeaderRow(title: string, costRaw = ''): boolean {
  const trimmed = title.trim();
  if (!trimmed || isNonPartSummaryRow(trimmed)) return false;
  if (!/:\s*$/.test(trimmed)) return false;
  const cost = parseCost(costRaw);
  // A priced line ending in ":" is unusual for a header — treat as a part.
  return cost === undefined;
}

export function isExcelFileName(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return lower.endsWith('.xlsx') || lower.endsWith('.xls');
}

export function validateExcelFile(file: File): string | null {
  if (!isExcelFileName(file.name)) {
    return 'File must be Excel (.xlsx or .xls)';
  }
  if (file.size > MAX_EXCEL_BYTES) {
    return `Excel file must be under ${Math.round(MAX_EXCEL_BYTES / (1024 * 1024))} MB`;
  }
  if (file.size === 0) {
    return 'Excel file is empty';
  }
  return null;
}

export function normalizeHeader(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[_#]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Map header labels to modification fields (first matching alias wins). */
export function mapHeaderColumns(headers: string[]): Partial<Record<ModField, number>> {
  const mapping: Partial<Record<ModField, number>> = {};

  headers.forEach((header, index) => {
    const normalized = normalizeHeader(header);
    if (!normalized) return;

    for (const [field, aliases] of Object.entries(COLUMN_ALIASES) as [ModField, string[]][]) {
      if (mapping[field] !== undefined) continue;
      if (aliases.some((alias) => normalized === alias || normalized.includes(alias))) {
        mapping[field] = index;
      }
    }
  });

  return mapping;
}

function cellValue(row: unknown[], index: number | undefined): string {
  if (index === undefined || index < 0 || index >= row.length) return '';
  const raw = row[index];
  if (raw === null || raw === undefined) return '';
  return String(raw).trim();
}

export function parseCost(raw: string): number | undefined {
  if (!raw) return undefined;
  const cleaned = raw.replace(/[^0-9.-]/g, '');
  if (!cleaned) return undefined;
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? num : undefined;
}

function formatCostForBuildList(cost: number): string {
  if (Number.isInteger(cost)) {
    return `$${cost.toLocaleString('en-US')}`;
  }
  return `$${cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Two-column shop sheets: PART DESCRIPTION + COST with "Section:" headers and a Total row.
 * Prefer this over flat structured JSON so the backend build-list parser can group categories.
 */
export function looksLikeSectionedCostList(
  dataRows: unknown[][],
  columnMap: Partial<Record<ModField, number>>
): boolean {
  if (columnMap.title === undefined || columnMap.cost === undefined) return false;
  // Explicit category/vendor columns mean a flat table, not a sectioned build sheet.
  if (columnMap.category !== undefined || columnMap.vendor !== undefined) return false;

  let sectionHeaders = 0;
  let partRows = 0;
  let summaryRows = 0;

  for (const row of dataRows) {
    if (!Array.isArray(row)) continue;
    const title = cellValue(row, columnMap.title);
    if (!title) continue;
    const costRaw = cellValue(row, columnMap.cost);
    if (isNonPartSummaryRow(title)) {
      summaryRows += 1;
      continue;
    }
    if (isSectionHeaderRow(title, costRaw)) {
      sectionHeaders += 1;
      continue;
    }
    partRows += 1;
  }

  if (sectionHeaders >= 2 && partRows >= 3) return true;
  if (sectionHeaders >= 1 && partRows >= 5 && summaryRows >= 1) return true;
  return false;
}

/** Convert a sectioned cost sheet into Notes-style build-list text. */
export function sectionedCostListToBuildText(
  dataRows: unknown[][],
  columnMap: Partial<Record<ModField, number>>
): { text: string; partCount: number } {
  const titleIndex = columnMap.title;
  if (titleIndex === undefined) return { text: '', partCount: 0 };

  const lines: string[] = [];
  let partCount = 0;
  let pendingBlank = false;

  for (const row of dataRows) {
    if (!Array.isArray(row)) continue;
    const title = cellValue(row, titleIndex);
    if (!title) {
      pendingBlank = true;
      continue;
    }
    const costRaw = cellValue(row, columnMap.cost);
    if (isNonPartSummaryRow(title)) {
      pendingBlank = true;
      continue;
    }

    if (isSectionHeaderRow(title, costRaw)) {
      if (lines.length > 0) lines.push('');
      const label = title.replace(/:\s*$/, '').trim();
      lines.push(`${label}:`);
      pendingBlank = false;
      continue;
    }

    if (pendingBlank && lines.length > 0 && lines[lines.length - 1] !== '') {
      // keep sections tight; blank only between sections
    }
    pendingBlank = false;

    const cost = parseCost(costRaw);
    const safeTitle = title.replace(/^\s*[-*•]\s*/, '').trim();
    if (cost !== undefined) {
      lines.push(`- ${safeTitle} - ${formatCostForBuildList(cost)}`);
    } else {
      lines.push(`- ${safeTitle}`);
    }
    partCount += 1;
  }

  return { text: lines.join('\n').trim(), partCount };
}

export function rowsToModifications(
  rows: unknown[][],
  columnMap: Partial<Record<ModField, number>>
): Array<Record<string, unknown>> {
  const titleIndex = columnMap.title;
  if (titleIndex === undefined) return [];

  const modifications: Array<Record<string, unknown>> = [];
  let currentGroup = '';

  for (const row of rows) {
    if (!Array.isArray(row)) continue;

    const title = cellValue(row, titleIndex);
    if (!title || normalizeHeader(title) === 'title' || normalizeHeader(title) === 'name') {
      continue;
    }
    if (isNonPartSummaryRow(title)) {
      continue;
    }

    const costRaw = cellValue(row, columnMap.cost);
    if (isSectionHeaderRow(title, costRaw)) {
      currentGroup = title.replace(/:\s*$/, '').trim();
      continue;
    }

    const specsRaw = cellValue(row, columnMap.specs);
    const notesRaw = cellValue(row, columnMap.notes);
    const groupRaw = cellValue(row, columnMap.group) || currentGroup;

    const mod: Record<string, unknown> = {
      title,
      category: cellValue(row, columnMap.category) || 'Miscellaneous',
      vendor: cellValue(row, columnMap.vendor) || '',
      cost: parseCost(costRaw),
      install_date: cellValue(row, columnMap.install_date) || undefined,
      part_number: cellValue(row, columnMap.part_number) || '',
    };

    if (groupRaw) {
      mod.group_label = groupRaw;
      mod.parentComponent = groupRaw;
    }
    if (specsRaw) {
      mod.specifications = specsRaw.split(/[;\n]/).map((s) => s.trim()).filter(Boolean);
    }
    if (notesRaw) {
      mod.notes = notesRaw;
    }

    if (mod.cost === undefined) {
      delete mod.cost;
    }
    if (!mod.install_date) {
      delete mod.install_date;
    }

    modifications.push(mod);
  }

  return modifications;
}

function sheetToMatrix(sheet: XLSX.WorkSheet): unknown[][] {
  return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false }) as unknown[][];
}

function scoreModSheet(matrix: unknown[][], sheetName: string): number {
  if (SKIP_SHEET_NAME.test(sheetName.trim())) return -100;
  if (/maintenance|service|fluids?|media|link/i.test(sheetName)) return -50;
  if (/mod/i.test(sheetName)) return 40;

  let score = 0;
  const headerRowIndex = matrix.findIndex((row) => {
    const map = mapHeaderColumns((row as unknown[]).map((cell) => String(cell ?? '')));
    return map.title !== undefined;
  });
  if (headerRowIndex < 0) return score;

  const headers = matrix[headerRowIndex].map((cell) => String(cell ?? ''));
  const columnMap = mapHeaderColumns(headers);
  if (columnMap.title !== undefined) score += 20;
  if (columnMap.cost !== undefined) score += 10;

  const dataRows = matrix.slice(headerRowIndex + 1);
  let sections = 0;
  let parts = 0;
  for (const row of dataRows) {
    if (!Array.isArray(row)) continue;
    const title = cellValue(row, columnMap.title);
    if (!title) continue;
    const costRaw = cellValue(row, columnMap.cost);
    if (isNonPartSummaryRow(title)) continue;
    if (isSectionHeaderRow(title, costRaw)) sections += 1;
    else parts += 1;
  }
  score += Math.min(sections, 12) * 3 + Math.min(parts, 80);
  return score;
}

/** Prefer the worksheet that looks like a build list (skip Fluids / Media / etc.). */
export function selectBestWorksheet(workbook: XLSX.WorkBook): { sheetName: string; sheet: XLSX.WorkSheet } {
  let bestName = workbook.SheetNames[0];
  let bestScore = Number.NEGATIVE_INFINITY;

  for (const name of workbook.SheetNames) {
    const sheet = workbook.Sheets[name];
    if (!sheet) continue;
    const matrix = sheetToMatrix(sheet).filter(
      (row) => Array.isArray(row) && row.some((cell) => String(cell ?? '').trim() !== '')
    );
    const score = scoreModSheet(matrix, name);
    if (score > bestScore) {
      bestScore = score;
      bestName = name;
    }
  }

  return { sheetName: bestName, sheet: workbook.Sheets[bestName] };
}

export function extractFromWorkbook(workbook: XLSX.WorkBook): ExcelExtractResult {
  if (!workbook.SheetNames.length) {
    throw new Error('Excel file has no worksheets');
  }

  const { sheetName, sheet } = selectBestWorksheet(workbook);
  const matrix = sheetToMatrix(sheet).filter((row) =>
    Array.isArray(row) && row.some((cell) => String(cell ?? '').trim() !== '')
  );

  if (matrix.length === 0) {
    throw new Error('Excel worksheet is empty');
  }

  const headerRowIndex = matrix.findIndex((row) => {
    const map = mapHeaderColumns(row.map((cell) => String(cell ?? '')));
    return map.title !== undefined;
  });

  if (headerRowIndex >= 0) {
    const headers = matrix[headerRowIndex].map((cell) => String(cell ?? ''));
    const columnMap = mapHeaderColumns(headers);
    const dataRows = matrix.slice(headerRowIndex + 1);

    if (looksLikeSectionedCostList(dataRows, columnMap)) {
      const { text, partCount } = sectionedCostListToBuildText(dataRows, columnMap);
      if (text && partCount > 0) {
        return {
          text,
          format: 'build_list',
          rowCount: partCount,
          sheetName,
        };
      }
    }

    const modifications = rowsToModifications(dataRows, columnMap);
    if (modifications.length > 0) {
      return {
        text: JSON.stringify({ modifications }, null, 2),
        format: 'structured_json',
        rowCount: modifications.length,
        sheetName,
      };
    }
  }

  // One-column build list: section headers + parts (no structured headers)
  const singleColumn = matrix.every((row) => {
    if (!Array.isArray(row)) return true;
    const filled = row.filter((cell) => String(cell ?? '').trim() !== '');
    return filled.length <= 1;
  });

  if (singleColumn) {
    const lines = matrix
      .map((row) => (Array.isArray(row) ? String(row[0] ?? '').trim() : ''))
      .filter(Boolean)
      .filter((line) => !isNonPartSummaryRow(line));
    if (lines.length > 0) {
      return {
        text: lines.join('\n'),
        format: 'build_list',
        rowCount: lines.length,
        sheetName,
      };
    }
  }

  const csv = XLSX.utils.sheet_to_csv(sheet).trim();
  if (!csv) {
    throw new Error('Could not read rows from Excel. Try exporting as CSV or paste the list manually');
  }

  const rowCount = csv.split('\n').filter((line) => line.trim()).length;

  return {
    text: csv,
    format: 'csv',
    rowCount,
    sheetName,
  };
}

export async function extractTextFromExcel(file: File): Promise<ExcelExtractResult> {
  const validationError = validateExcelFile(file);
  if (validationError) {
    throw new Error(validationError);
  }

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });

  return extractFromWorkbook(workbook);
}
